/* eslint-disable max-lines */
/* eslint-disable no-console */
'use client';
import { initializeApp } from 'firebase/app';
import { GoogleAuthProvider, getAuth, signInWithPopup, signOut as signOutAuth } from 'firebase/auth';
import {
  DocumentData,
  Query,
  Timestamp,
  Unsubscribe,
  WriteBatch,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  endBefore,
  getDoc,
  getDocs,
  getFirestore,
  increment,
  limit,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

import { ChatData, ListingData, Message, MessageWithReceiver, User, UserChat } from '@/slw/entities/chat';

import { uploadFiles } from '../../api/api.service';
import { UPLOAD_FILE_CODE } from '../../consts/file.const';
import { useAuthStore } from '../../stores';
import { checkExistingChat } from './check-existing-chat';
import { app, auth as configAuth, db as configDb, storage as configStorage, vapidKey } from './config';
import { messageConverter } from './converter';

// Use app from config.ts to avoid duplicate initialization
const messaging = () => {
  if (!app) {
    throw new Error('Firebase app is not initialized');
  }
  return getMessaging(app);
};

// Use instances from config.ts to avoid duplicate initialization
// If they are null, Firebase features will be disabled
export const auth = configAuth;
export const db = configDb;
export const storage = configStorage;

// Lazy initialization to avoid errors when db is null
export const getMessagesRef = () => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  return collection(db, 'chats');
};

export const getTokensRef = () => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  return collection(db, 'tokens');
};

// Keep for backward compatibility, but use getters
export const messagesRef = db ? collection(db, 'chats') : null as any;
export const tokensRef = db ? collection(db, 'tokens') : null as any;

export function signIn(): void {
  const provider = new GoogleAuthProvider();

  void signInWithPopup(auth, provider);
}

export function signOut(): void {
  void signOutAuth(auth);
}
export async function getLatestMessages(
  chatId: string,
  limitCount = 10,
): Promise<{
  messages: Message[];
  firstDoc: DocumentData | null;
  lastDoc: DocumentData | null;
  hasMore: boolean;
}> {
  try {
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(limitCount)).withConverter(messageConverter);

    const snapshot = await getDocs(q);
    const messages: Message[] = [];

    snapshot.forEach((doc) => {
      messages.push({
        ...doc.data(),
      });
    });

    return {
      messages: messages.reverse(), // Đảo ngược để hiển thị đúng thứ tự thời gian
      firstDoc: snapshot.docs[snapshot.docs.length - 1], // Document đầu tiên (cũ nhất)
      lastDoc: snapshot.docs[0], // Document cuối cùng (mới nhất)
      hasMore: snapshot.docs.length === limitCount,
    };
  } catch (error) {
    return { messages: [], firstDoc: null, lastDoc: null, hasMore: false };
  }
}

export async function getOlderMessages(chatId: string, firstDoc: DocumentData, limitCount = 10) {
  try {
    if (!firstDoc) return { messages: [], firstDoc: null, hasMore: false };

    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(
      messagesRef,
      orderBy('createdAt', 'desc'),
      startAfter(firstDoc), // Bắt đầu sau document cũ nhất hiện tại
      limit(limitCount),
    ).withConverter(messageConverter);

    const snapshot = await getDocs(q);
    const messages: Message[] = [];

    snapshot.forEach((doc) => {
      messages.push({
        ...doc.data(),
      });
    });

    return {
      messages: messages.reverse(), // Đảo ngược để hiển thị đúng thứ tự
      firstDoc: snapshot.docs[snapshot.docs.length - 1], // Document cũ nhất mới
      hasMore: snapshot.docs.length === limitCount,
    };
  } catch (error) {
    return { messages: [], firstDoc: null, hasMore: false };
  }
}

export function listenToNewMessages(
  chatId: string,
  lastDoc: DocumentData | null,
  callback: (messages: Message[], newLastDoc: DocumentData) => void,
) {
  if (!lastDoc) return () => {};

  const messagesRef = collection(db, 'chats', chatId, 'messages');
  const q = query(messagesRef, orderBy('createdAt', 'desc'), endBefore(lastDoc)).withConverter(messageConverter);
  let newLastDoc = null;

  return onSnapshot(q, (snapshot) => {
    const newMessages: Message[] = [];

    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        newMessages.push({
          ...change.doc.data(),
        });
      }
    });

    newLastDoc = snapshot.docs[0];

    if (newMessages.length > 0) {
      callback(newMessages.reverse(), newLastDoc);
    }
  });
}

export function listenToNotifications(
  userId: string,
  callback: (data: { notificationCount: number; success: boolean }) => void,
) {
  if (!userId) return () => {};

  const userChatsRef = doc(db, 'userchats', userId);

  const unsubscribe = onSnapshot(
    userChatsRef,
    (doc) => {
      if (!doc.exists()) {
        callback({
          success: true,
          notificationCount: 0,
        });

        return;
      }

      const userData = doc.data();
      const totalNotifications = userData.totalUnreadNotifications || 0;

      // Get unread chats

      callback({
        success: true,
        notificationCount: totalNotifications,
      });
    },
    (error) => {
      callback({
        success: false,
        notificationCount: 0,
      });
    },
  );

  return unsubscribe;
}

export function getMessagesQuery(many: number, chatId: string): Query<Message> {
  return query(
    collection(db, 'chats', chatId, 'messages'),
    orderBy('createdAt', 'asc'),
    limitToLast(many),
  ).withConverter(messageConverter);
}

export async function getMessagesSize(chatId: string): Promise<number> {
  try {
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);

    if (chatSnap.exists()) {
      return chatSnap.data().messageCount || 0;
    }

    return 0;
  } catch (error) {
    return 0;
  }
}

async function updateSenderUserChats(
  senderUserId: string,
  messageData: { chatId: string },
  receiverUserId: string,
  batch: WriteBatch,
) {
  try {
    const senderUserChatsRef = doc(db, 'userchats', senderUserId);
    const senderUserChatsSnap = await getDoc(senderUserChatsRef);

    if (!senderUserChatsSnap.exists()) {
      await batch.set(
        senderUserChatsRef,
        {
          chats: arrayUnion([
            {
              ...messageData,
              receiverId: receiverUserId || '',
              isSeen: true,
              isNotificationRead: true,
              createdAt: Timestamp.fromDate(new Date()),
              updatedAt: Timestamp.fromDate(new Date()),
            },
          ]),
          totalUnreadNotifications: 0,
          lastModified: serverTimestamp(),
        },
        { merge: true },
      );

      return;
    }

    const senderData = senderUserChatsSnap.data();
    const senderChats = senderData.chats || [];
    const chatIndex = senderChats.findIndex((chat: ChatData) => chat.chatId === messageData.chatId);

    if (chatIndex >= 0) {
      // Update existing chat
      senderChats[chatIndex] = {
        ...senderChats[chatIndex],
        ...messageData,
        receiverId: receiverUserId || '',
        isSeen: true,
        isNotificationRead: true,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };
    } else {
      // Add new chat
      senderChats.push({
        ...messageData,
        receiverId: receiverUserId || '',
        isSeen: true,
        isNotificationRead: true,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    }

    await batch.set(
      senderUserChatsRef,
      {
        ...senderData,
        chats: senderChats,
        totalUnreadNotifications: senderData.totalUnreadNotifications || 0,
        lastModified: Timestamp.fromDate(new Date()),
      },
      { merge: true },
    );
  } catch (error) {
    // Error updating sender userchats
  }
}

async function updateReceiverUserChatsWithNotification(
  receiverUserId: string,
  messageData: { chatId: string },
  senderUserId: string,
  batch: WriteBatch,
) {
  try {
    const receiverUserChatsRef = doc(db, 'userchats', receiverUserId);
    const receiverUserChatsSnap = await getDoc(receiverUserChatsRef);

    if (!receiverUserChatsSnap.exists()) {
      // Create new userchats document for receiver
      await batch.set(receiverUserChatsRef, {
        chats: [
          {
            ...messageData,
            receiverId: senderUserId || '',
            isSeen: false,
            isNotificationRead: false,
            createdAt: Timestamp.fromDate(new Date()),
            updatedAt: Timestamp.fromDate(new Date()),
          },
        ],
        totalUnreadNotifications: 1,
        lastModified: Timestamp.fromDate(new Date()),
      });

      return;
    }

    const receiverData = receiverUserChatsSnap.data();
    const receiverChats = receiverData.chats || [];
    const chatIndex = receiverChats.findIndex((chat: ChatData) => chat.chatId === messageData.chatId);

    let totalUnreadNotifications = receiverData.totalUnreadNotifications || 0;

    if (chatIndex >= 0) {
      const existingChat = receiverChats[chatIndex];
      const wasNotificationRead = existingChat.isNotificationRead || false;

      receiverChats[chatIndex] = {
        ...messageData,
        receiverId: senderUserId || '',
        isSeen: false,
        isNotificationRead: false,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      if (wasNotificationRead) totalUnreadNotifications += 1;
    } else {
      receiverChats.push({
        ...messageData,
        receiverId: senderUserId || '',
        isSeen: false,
        isNotificationRead: false,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      totalUnreadNotifications += 1;
    }

    await batch.set(receiverUserChatsRef, {
      ...receiverData,
      chats: receiverChats,
      totalUnreadNotifications,
      lastModified: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    // Error updating receiver userchats with notification
  }
}

export async function createChatWithListing({
  listingData,
  sender,
  receiver,
}: {
  listingData: ListingData;
  sender: User;
  receiver: User;
}) {
  try {
    const existingChat = await checkExistingChat(sender.id, receiver.id, listingData.listingId);

    if (existingChat.exists && existingChat.chatId && existingChat.hasListingId) {
      return {
        success: true,
        chatId: existingChat.chatId,
        message: 'Existing chat found',
        isExisting: true,
        receiverUser: existingChat.receiverUser,
      };
    } else if (existingChat.exists && existingChat.chatId && !existingChat.hasListingId) {
      sendMessage({
        text: null,
        imageData: null,
        listingData,
        sender,
        receiver,
        chatId: existingChat.chatId,
      });

      return {
        success: true,
        chatId: existingChat.chatId,
        message: 'New listing message added to existing chat',
        isExisting: true,
        hasListingMessage: false,
        receiverUser: existingChat.receiverUser,
      };
    } else {
      const batch = writeBatch(db);

      const chatRef = doc(collection(db, 'chats'));
      const chatId = chatRef.id;

      // const userChatsRef = collection(db, 'userchats');
      const messageRef = doc(collection(db, 'chats', chatId, 'messages'));

      batch.set(chatRef, {
        createdAt: serverTimestamp(),
        messages: [],
        messageCount: 1,
      });
      batch.set(messageRef, {
        senderId: sender.id,
        senderName: sender.username,
        receiverId: receiver.id,
        receiverName: receiver.username,
        avatar: sender.avatar || '',
        text: null,
        createdAt: new Date(),
        imgUrls: null,
        id: uuidv4(),
        listingData: {
          url: listingData.url,
          title: listingData.title,
          imageUrl: listingData.imageUrl,
          listingId: listingData.listingId,
          price: listingData.price,
        },
      });

      const chatData = {
        chatId,
        lastMessage: 'Vehicle link',
        updatedAt: new Date(),
      };

      // await batch.set(
      //   doc(userChatsRef, receiver?.id || ''),
      //   {
      //     chats: arrayUnion({
      //       ...chatData,
      //       receiverId: sender?.id || '',
      //       isSeen: false,
      //       isNotificationRead: false,
      //       createdAt: new Date(),
      //       updatedAt: new Date(),
      //     }),
      //   },
      //   { merge: true },
      // );

      await updateSenderUserChats(sender?.id, chatData, receiver.id, batch);
      await updateReceiverUserChatsWithNotification(receiver?.id, chatData, sender.id, batch);

      // await batch.set(
      //   doc(userChatsRef, sender?.id || ''),
      //   {
      //     chats: arrayUnion({
      //       ...chatData,
      //       receiverId: receiver.id || '',
      //       isSeen: true,
      //       isNotificationRead: true,
      //       createdAt: new Date(),
      //       updatedAt: new Date(),
      //     }),
      //   },
      //   { merge: true },
      // );

      await batch.commit();
      const receiverUserRef = doc(db, 'users', receiver.id);
      const receiverUserSnap = await getDoc(receiverUserRef);
      let receiverUser: User | null = null;

      if (receiverUserSnap.exists()) {
        receiverUser = {
          ...receiverUserSnap.data(),
        } as User;
      }

      return {
        success: true,
        chatId,
        message: 'Chat created successfully',
        isExisting: false,
        receiverUser,
      };
    }
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
      receiverUser: null,
    };
  }
}

export async function findOtherUserIdFromUserChats(chatId: string, currentUserId: string) {
  try {
    const currentUserChatRef = doc(db, 'userchats', currentUserId);
    const currentUserChatSnap = await getDoc(currentUserChatRef);

    if (!currentUserChatSnap.exists()) {
      return {
        success: false,
        message: 'Current user chats not found',
      };
    }

    const currentUserData = currentUserChatSnap.data();
    const currentUserChats = currentUserData.chats || [];

    const targetChat = currentUserChats.find((chat: ChatData) => chat.chatId === chatId);

    if (!targetChat) {
      return {
        success: false,
        message: 'Chat not found in current user chats',
      };
    }

    const otherUserId = targetChat.receiverId;

    return {
      success: true,
      otherUserId,
      chatInfo: targetChat,
    };
  } catch (error) {
    return {
      success: false,
    };
  }
}

export async function checkLastMessageSeenStatus(chatId: string, messageId: string, currentUserId: string) {
  try {
    const { otherUserId } = await findOtherUserIdFromUserChats(chatId, currentUserId);

    // Lấy userchats của người khác để check isSeen
    const otherUserChatRef = doc(db, 'userchats', otherUserId);
    const otherUserChatSnap = await getDoc(otherUserChatRef);

    if (!otherUserChatSnap.exists()) {
      return {
        success: false,
        message: 'Other user chats not found',
      };
    }

    const otherUserData = otherUserChatSnap.data();
    const otherUserChats = otherUserData.chats || [];

    // Tìm chat tương ứng
    const correspondingChat = otherUserChats.find((chat: UserChat) => chat.chatId === chatId);

    if (!correspondingChat) {
      return {
        success: false,
        message: 'Corresponding chat not found',
      };
    }

    // Lấy message cuối cùng để check xem có phải của mình gửi không
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(1));
    const messagesSnap = await getDocs(q);

    if (messagesSnap.empty) {
      return {
        success: false,
        message: 'No messages found',
      };
    }

    const lastMessage = messagesSnap.docs[0].data();

    const isMyLastMessage = lastMessage.senderId === currentUserId && messageId === lastMessage.id;

    return {
      success: true,
      isMyLastMessage,
      isSeen: correspondingChat.isSeen,
      lastMessage,
      otherUserId,
      chatInfo: correspondingChat,
    };
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

// ===============================
// 2. REAL-TIME LISTENER CHO SEEN STATUS
// ===============================

export function listenToSeenStatus(
  chatId: string,
  currentUserId: string,
  callback: ({
    success,
    isSeen,
    otherUserId,
    chatInfo,
  }: {
    success: boolean;
    isSeen?: boolean;
    otherUserId?: string;
    chatInfo?: UserChat;
    message?: string;
    error?: string;
  }) => void,
): Promise<Unsubscribe> {
  return new Promise(async (resolve) => {
    try {
      const { otherUserId } = await findOtherUserIdFromUserChats(chatId, currentUserId);
      // Listen to other user's userchats document
      const otherUserChatRef = doc(db, 'userchats', otherUserId);

      const unsubscribe = onSnapshot(
        otherUserChatRef,
        (docSnap) => {
          if (!docSnap.exists()) {
            callback({ success: false, message: 'Other user chats not found' });

            return;
          }

          const otherUserData = docSnap.data();
          const otherUserChats = otherUserData.chats || [];
          const correspondingChat = otherUserChats.find((chat: MessageWithReceiver) => chat.chatId === chatId);

          if (correspondingChat) {
            callback({
              success: true,
              isSeen: correspondingChat.isSeen,
              otherUserId,
              chatInfo: correspondingChat,
              message: 'success',
            });
          }
        },
        (error) => {
          callback({ success: false, error: (error as Error).message });
        },
      );

      resolve(unsubscribe);
    } catch (error) {
      callback({ success: false, error: (error as Error).message });
      resolve(() => {});
    }
  });
}

export async function sendMessage({
  text,
  imageData,
  chatId,
  sender,
  receiver,
  listingData,
}: {
  text: string | null;
  imageData: string[] | null;
  chatId: string;
  sender: User;
  receiver: User;
  listingData?: ListingData;
}) {
  const batch = writeBatch(db);
  const chatRef = doc(db, 'chats', chatId);
  const messageRef = doc(collection(db, 'chats', chatId, 'messages'));

  await markMessageAsSeen(chatId, sender.id);

  const message = {
    senderId: sender.id,
    senderName: sender.username,
    receiverId: receiver.id,
    receiverName: receiver.username,
    avatar: sender.avatar || '',
    text: !listingData?.listingId ? text : null,
    createdAt: new Date(),
    imgUrls: !listingData?.listingId ? imageData : null,
    id: uuidv4(),
    chatId: chatRef.id,
    ...(listingData?.listingId && {
      listingData: {
        url: listingData.url,
        title: listingData.title,
        imageUrl: listingData.imageUrl,
        listingId: listingData.listingId,
        price: listingData.price,
      },
    }),
  };

  await batch.set(messageRef, message);

  await batch.update(chatRef, {
    messageCount: increment(1),
  });

  // const chatRef = await updateDoc(doc(db, 'chats', chatId, 'messages'), {
  //   messages: arrayUnion({
  //     senderId: sender.id,
  //     senderName: sender.username,
  //     avatar: sender.avatar || '',
  //     text,
  //     createdAt: new Date(),
  //     imgUrls: imageData,
  //     id: uniqueId(),
  //   }),
  // });

  const chatData = {
    chatId,
    lastMessage: listingData?.listingId ? 'Vehicle link' : text || imageData,
    updatedAt: Timestamp.fromDate(new Date()),
  };

  await updateSenderUserChats(sender?.id, chatData, receiver.id, batch);
  await updateReceiverUserChatsWithNotification(receiver?.id, chatData, sender.id, batch);

  // const userIDs = [sender.id, receiver.id];

  // userIDs.forEach(async (id) => {
  //   const userChatsRef = doc(db, 'userchats', id);
  //   const userChatsSnapshot = await getDoc(userChatsRef);

  //   if (userChatsSnapshot.exists()) {
  //     const userChatsData = userChatsSnapshot.data();

  //     const chatIndex = userChatsData.chats.findIndex((c: Chat) => c.chatId === chatId);

  //     userChatsData.chats[chatIndex].lastMessage = text || imageData;
  //     userChatsData.chats[chatIndex].isSeen = id === sender.id ? true : false;
  //     userChatsData.chats[chatIndex].isNotificationRead = id === sender.id ? true : false;
  //     userChatsData.chats[chatIndex].updatedAt = Date.now();
  //     userChatsData.chats[chatIndex].createdAt = Date.now();

  //     userChatsData.chats[chatIndex].senderId = sender.id;
  //     // userChatsData.chats[chatIndex].receiverId = receiver.id;

  //     await updateDoc(userChatsRef, {
  //       chats: userChatsData.chats,
  //     });
  //   }
  // });
  await batch.commit();

  return chatRef;
}

export async function markAllNotificationsAsRead(userId: string) {
  try {
    const userChatsRef = doc(db, 'userchats', userId);
    const userChatsSnap = await getDoc(userChatsRef);

    if (!userChatsSnap.exists()) {
      return { success: false, message: 'User chats not found' };
    }

    const userData = userChatsSnap.data();
    const userChats = userData.chats || [];

    // Mark all chats as notification read
    const updatedChats = userChats.map((chat: any) => ({
      ...chat,
      isNotificationRead: true,
    }));

    await setDoc(userChatsRef, {
      ...userData,
      chats: updatedChats,
      totalUnreadNotifications: 0,
      lastModified: serverTimestamp(),
    });

    return {
      success: true,
      message: 'All notifications marked as read',
    };
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

export async function markMessageAsSeen(chatId: string, userId: string) {
  try {
    const userChatsRef = doc(db, 'userchats', userId);
    const userChatsSnap = await getDoc(userChatsRef);

    if (!userChatsSnap.exists()) {
      return { success: false, message: 'User chats not found' };
    }

    const userData = userChatsSnap.data();
    const userChats = userData.chats || [];
    const chatIndex = userChats.findIndex((chat: ChatData) => chat.chatId === chatId);

    if (chatIndex === -1) {
      return { success: false, message: 'Chat not found' };
    }

    const targetChat = userChats[chatIndex];

    if (targetChat.isSeen === true) {
      return { success: true, message: 'Already marked as seen' };
    }

    let totalUnreadNotifications = userData.totalUnreadNotifications || 0;
    const wasNotificationUnread = !targetChat.isNotificationRead;

    if (wasNotificationUnread && totalUnreadNotifications > 0) {
      totalUnreadNotifications -= 1;
    }

    userChats[chatIndex] = {
      ...targetChat,
      isSeen: true,
      isNotificationRead: true,
      updatedAt: Timestamp.fromDate(new Date()),
    };

    await updateDoc(userChatsRef, {
      ...userData,
      chats: userChats,
      lastModified: serverTimestamp(),
      totalUnreadNotifications,
    });

    return {
      success: true,
      message: 'Message marked as seen',
    };
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

export function deleteMessage(docId: string): void {
  const docRef = doc(messagesRef, docId);

  void deleteDoc(docRef);
}

export function editMessage(docId: string, text: string): void {
  const docRef = doc(messagesRef, docId);

  void updateDoc(docRef, {
    text,
    editedAt: serverTimestamp(),
  });
}

export async function sendImages({
  text,
  selectedImages,
  chatId,
  sender,
  receiver,
}: {
  text: string | null;
  selectedImages: File[];
  chatId: string;
  sender: User;
  receiver: User;
}): Promise<void> {
  try {
    const result = await uploadFiles({
      files: selectedImages,
      applicationCode: UPLOAD_FILE_CODE.listing.application,
      moduleCode: UPLOAD_FILE_CODE.listing.module,
      functionCode: UPLOAD_FILE_CODE.listing.function,
    });

    if (result.success && result.data) {
      await sendMessage({
        text,
        imageData: result.data.map((item) => `${item.url}${item.filePath}`),
        chatId,
        sender,
        receiver,
      });
    }
  } catch (error) {
    // Error sending images to server
  }
}

export const upload = async (file: File) => {
  const date = new Date();
  const storageRef = ref(storage, `images/${date + file.name}`);

  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        // Upload progress
      },
      (error) => {
        reject('Something went wrong!' + error.code);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          resolve(downloadURL);
        });
      },
    );
  });
};

export async function deleteAllMessages(): Promise<void> {
  const docsRef = await getDocs(messagesRef);

  await Promise.all(docsRef.docs.map(({ id }) => deleteMessage(id)));
}

export async function saveMessagingDeviceToken(): Promise<void> {
  try {
    // Skip FCM if VAPID key is not configured
    if (!vapidKey) {
      return;
    }

    const currentToken = await getToken(messaging(), { vapidKey });

    if (currentToken) {
      const currentUser = useAuthStore.getState().user!;
      const setFcmToken = useAuthStore.getState().setFcmToken;


      setFcmToken(currentToken);

      const tokenRef = doc(db, 'tokens', currentToken);

      await setDoc(
        tokenRef,
        {
          name: currentUser?.firstName,
          id: currentUser?.id,
        },
        { merge: true },
      );
    } else void requestNotificationsPermissions();
  } catch (error: any) {
    // Silently handle FCM errors (401, permission denied, etc.)
    // This is expected if VAPID key is not configured or FCM is not enabled
    if (error?.code !== 'messaging/permission-blocked' && error?.code !== 'messaging/unsupported-browser') {
      // Only log unexpected errors in development
      if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        // Log in development only
      }
    }
  }
}

export async function requestNotificationsPermissions(): Promise<void> {
  // Skip if VAPID key is not configured
  if (!vapidKey) {
    return;
  }

  const permission = await Notification.requestPermission();

  if (permission === 'granted') {
    await saveMessagingDeviceToken();
  } else {
    // Unable to get permission to notify
  }
}

export const onMessageListener = () =>
  new Promise((resolve) => {
    onMessage(messaging(), (payload: any) => {
      resolve(payload);
    });
  });
